import { useMemo } from 'react';
import cn from 'classnames';
import { IconDrag } from '@direct-frontend/components';
import { type TreeRow } from '../../lib/types';
import { type DraggableRowProps } from './types';
import styles from './DraggableRowOverlay.module.css';

export interface DraggableRowOverlayProps<Attributes> extends DraggableRowProps<Attributes> {
    isExpanded?: boolean;
    insertAsChild?: boolean;
    overNode: TreeRow<Attributes> | undefined;
    settings?: {
        dragIcon?: React.ReactNode;
    };
}

export const DraggableRowOverlay = <Attributes = unknown,>({
    'data-testid': dataTestId = 'DraggableRowOverlay',
    size,
    isExpanded = true,
    node,
    renderNode,
    insertAsChild = false,
    overNode,
    settings,
    IconDnd = IconDrag,
    iconSize = '20',
}: DraggableRowOverlayProps<Attributes>) => {
    const expectedNode = useMemo(() => {
        if (!overNode || overNode.id === node.id) {
            return node;
        }

        if (insertAsChild) {
            return {
                ...node,
                rendersBefore: [...overNode.rendersBefore, { id: overNode.id, vertical: false }],
            };
        }

        return {
            ...node,
            rendersBefore: overNode.rendersBefore,
        };
    }, [insertAsChild, node, overNode]);

    return (
        <div data-testid={`${dataTestId}`} className={styles.container}>
            {renderNode({
                size,
                node: expectedNode,
                beforeAddon: (
                    <div className={cn(styles.dragIconWrapper, { [styles.sizeM]: size === 'm' })}>
                        {settings?.dragIcon || <IconDnd size={iconSize} role="main" color="gray" />}
                    </div>
                ),
                'data-testid': `${dataTestId}.Item`,
                isSelected: false,
                isOpen: isExpanded,
                isOpenDisabled: true,
                isDnDStub: true,
            })}
        </div>
    );
};
